import React from 'react'
import { Router, RouteComponentProps, NavigateFn } from '@reach/router'
import Appbar from '../../Appbar'
import { Tag, ProgressBar } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import {
  PageContent,
  Flex,
  Button,
  IconButton,
  ModalLayer,
  PrettyNumber,
  PrettyDate,
  FileDownloadLink,
  NewGridTable,
  IGridTableColumn,
  Skeleton,
} from '@ui'
import { Permissions, PermissionsType, IPermissionsOnEmployeeList } from '@permissions'
import useImportHistory from './hooks/useImportHistory'
import useImportProgress from '../../ImportFlow/hooks/usePollStatus'
import api, { INeighborhoodImport } from '@api'
import track from '@track'
import Event from '@track/Event'
import { DateUtil, DATE_PARSE_FORMAT } from '@utils'
import css from './importHistory.module.scss'

const statusIntents: { [key: string]: 'success' | 'danger' } = {
  completed: 'success',
  failed: 'danger',
}

export default function ImportHistoryPage({ navigate }: RouteComponentProps) {
  const { imports, pageInfo, onNextPage, status, reload } = useImportHistory()
  const columns: IGridTableColumn<INeighborhoodImport>[] = [
    {
      label: 'Import',
      Cell: ({ skeleton, cell }) =>
        skeleton ? (
          <Skeleton />
        ) : cell?.data.status === 'completed' ? (
          <CompletedImportLink i={cell.data} navigate={navigate} />
        ) : cell?.data.status === 'failed' ? (
          <FailedImportLink i={cell.data} />
        ) : cell?.data ? (
          <UpdatingImportLink i={cell?.data} navigate={navigate} />
        ) : null,
    },
    {
      label: 'Total',

      Cell: ({ skeleton, cell }) =>
        skeleton ? <Skeleton /> : <PrettyNumber value={cell?.data.totalCount ?? 0} />,
    },
    {
      label: 'Created',

      Cell: ({ skeleton, cell }) =>
        skeleton ? <Skeleton /> : <PrettyNumber value={cell?.data.createdCount ?? 0} />,
    },
    {
      label: 'Failed',

      Cell: ({ skeleton, cell }) =>
        skeleton ? <Skeleton /> : <PrettyNumber value={cell?.data.failedCount ?? 0} />,
    },
    {
      label: 'Files',
      Cell: ({ skeleton, cell }) =>
        skeleton ? (
          <Skeleton />
        ) : (
          <span
            style={{ whiteSpace: 'nowrap' }}
            onClick={() => {
              track.neighborhoodImportHistory({
                eventName: Event.IMPORT,
                props: {
                  type: 'file',
                },
              })
            }}
          >
            {cell?.data.url && (
              <Tooltip2 content="Download imported file">
                <FileDownloadLink url={cell?.data.url}>Imported</FileDownloadLink>
              </Tooltip2>
            )}
          </span>
        ),
    },
    {
      label: 'Status',
      Cell: ({ skeleton, cell }) =>
        skeleton ? (
          <Skeleton />
        ) : cell?.data.status === 'completed' || cell?.data.status === 'failed' ? (
          <Tag
            round
            minimal
            intent={statusIntents[cell?.data.status]}
            style={{ textTransform: 'capitalize' }}
          >
            {cell?.data.status}
          </Tag>
        ) : (
          <StatusProgress id={cell?.data.id ?? ''} />
        ),
    },
  ]

  return (
    <>
      <Appbar
        title={
          <Flex>
            <span style={{ marginRight: 8 }}>
              <Tooltip2 content="Back to Neighborhood Management">
                <IconButton
                  minimal
                  iconName="chevron-left"
                  onClick={() => {
                    navigate?.('/neighborhoods')

                    track.neighborhoodImportHistory({
                      eventName: Event.NAVIGATE,
                      props: {
                        pageName: '/neighborhoods',
                      },
                    })
                  }}
                />
              </Tooltip2>
            </span>
            Import History
          </Flex>
        }
      />
      {/* eslint-disable @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <Router primary={false} navigate={navigate}>
        <ModalLayer path="*" reload={reload} />
      </Router>
      <PageContent>
        <Toolbar navigate={navigate} />
        <NewGridTable
          data={imports}
          columns={columns}
          status={status}
          page={{
            hasNextPage: pageInfo?.hasNextPage || false,
            endCursor: pageInfo?.endCursor || '',
            onNextPage,
          }}
          headerTop={52}
        />
      </PageContent>
    </>
  )
}

function StatusProgress({ id }: { id: string }) {
  const { progress, newStatus } = useImportProgress(id, api.import.progress)
  if (newStatus === 'completed' || newStatus === 'failed') {
    return (
      <Tag round minimal intent={statusIntents[newStatus]} style={{ textTransform: 'capitalize' }}>
        {newStatus}
      </Tag>
    )
  } else if (newStatus === 'canceled') {
    return (
      <Tooltip2 content="File has been Canceled">
        <Tag round minimal>
          Canceled
        </Tag>
      </Tooltip2>
    )
  } else if (progress === null) {
    return (
      <Tooltip2 content="File is waiting to be processed">
        <Tag round minimal>
          Pending
        </Tag>
      </Tooltip2>
    )
  } else {
    return <ProgressBar value={progress} stripes={false} />
  }
}

function FailedImportLink({ i }: { i: INeighborhoodImport }) {
  return (
    <div>
      <PrettyDate value={i.createdAt} /> {DateUtil(i.createdAt).timeFormat()}
      <span style={{ color: 'var(--grey1)' }}> by {i.importedBy}</span>
    </div>
  )
}

function CompletedImportLink({ i, navigate }: { i: INeighborhoodImport; navigate?: NavigateFn }) {
  return (
    <div
      style={{ cursor: 'pointer' }}
      onClick={() => {
        navigate?.(`/neighborhoods/history/${i.id}`)

        track.neighborhoodImportHistory({
          eventName: Event.NAVIGATE,
          props: {
            pageName: `/neighborhoods/history/${i.id}`,
          },
        })
      }}
    >
      <Tooltip2 content="Go to Import Details">
        <div className="cursor-pointer hover:underline">
          <PrettyDate value={i.createdAt} /> @ {DateUtil(i.createdAt).timeFormat()}
        </div>
      </Tooltip2>
      <span style={{ color: 'var(--grey1)' }}> by {i.importedBy}</span>
    </div>
  )
}

function UpdatingImportLink({ i, navigate }: { navigate?: NavigateFn; i: INeighborhoodImport }) {
  const { newStatus } = useImportProgress(i.id, api.import.progress)

  return newStatus === 'completed' ? (
    <CompletedImportLink i={i} navigate={navigate} />
  ) : newStatus === 'failed' ? (
    <FailedImportLink i={i} />
  ) : (
    <div
      style={{ cursor: 'pointer' }}
      onClick={() => {
        navigate?.('?newItem=neighborhoodImport')

        track.neighborhoodImportDialog({
          eventName: Event.NEW_RECORD_DIALOG,
        })
      }}
    >
      <PrettyDate value={i.createdAt} /> @ {DateUtil(i.createdAt).timeFormat()}
      <span style={{ color: 'var(--grey1)' }}> by {i.importedBy}</span>
    </div>
  )
}

function Toolbar({ navigate }: { navigate?: NavigateFn }) {
  return (
    <Permissions type={PermissionsType.EMPLOYEE_LIST} on={[]}>
      {(permissions) => (
        <Flex className={css.importHistoryToolbar}>
          <div />
          {(permissions as IPermissionsOnEmployeeList).canImport && (
            <Button
              text="New Import"
              iconName="plus"
              onClick={() => {
                navigate?.('?newItem=neighborhoodsImport')

                track.neighborhoodImportDialog({
                  eventName: Event.NEW_RECORD_DIALOG,
                })
              }}
            />
          )}
        </Flex>
      )}
    </Permissions>
  )
}
